<template>
    <section class="w-80 inline-flex flex-col justify-start items-center gap-12">
        <div class="self-stretch inline-flex flex-col justify-start items-start gap-6">
            <div class="self-stretch justify-start text-black text-3xl font-medium font-syne">
                <h1>Log in</h1>
            </div>
            <CompanyDisplay
                :tenant-name="tenant?.name ?? ''"
                :tenant-domain="tenant?.domain ?? ''"
                :logo-uri="tenant?.logoUrl ?? ''"/>
        </div>

        <UAlert
            v-if="submissionError"
            color="error"
            class="self-stretch h-10 px-4 bg-red-100 rounded-[20px] inline-flex justify-start items-center gap-3 overflow-hidden"
            icon="ph:warning"
            :ui="{
                icon: 'text-red-700'
            }">
            <template #title>
                <div class="justify-start text-red-700 text-sm font-normal font-dm-sans">
                    {{ submissionError }}
                </div>
            </template>
        </UAlert>
        <UForm
            :state="state"
            :schema="loginSchema"
            autocomplete="off"
            class="self-stretch inline-flex flex-col justify-start items-start gap-4"
            @submit="handleLogin">
            <InputField
                v-model="state.username"
                name="username"
                label="Work email"
                placeholder="alex@example.com"
                icon="ph:password"/>
            <InputField
                v-model="state.password"
                name="password"
                label="Password"
                placeholder="••••••••"
                icon="ph:password"
                :type="showPassword ? 'text': 'password'"
            >
                <template #trailing>
                    <UButton
                        color="neutral"
                        variant="link"
                        size="md"
                        :icon="showPassword ? 'ph:eye-slash' : 'ph:eye'"
                        :ui="{
                            base: 'p-3 cursor-pointer'
                        }"
                        @click="showPassword = !showPassword"
                    />
                </template>
            </InputField>
            <UButton
                type="submit"
                class="self-stretch px-4 h-10 rounded-3xl bg-blue-900 hover:bg-blue-800 focus-visible:outline-3 focus-visible:outline-blue-300 focus-visible:outline-offset-0 disabled:bg-neutral-100 disabled:text-neutral-400 inline-flex justify-center items-center gap-2.5 overflow-hidden cursor-pointer"
            >
                Log in
            </UButton>
            <div class="self-stretch text-center justify-end">
                <span class="text-navy-600 text-sm font-normal font-dm-sans">Forgot your password? </span>
                <span class="text-navy-700 text-sm font-medium font-dm-sans underline">
                    <NuxtLink :to="localePath('domain-reset-password')">Reset it</NuxtLink></span>
            </div>
        </UForm>
        
        <div class="self-stretch inline-flex justify-start items-center gap-2.5">
            <div class="flex-1 h-0 outline-1 outline-offset-[-0.50px] outline-neutral-300"/>
            <div class="h-3.5 justify-end pb-5 text-neutral-500 text-sm font-normal font-dm-sans">or</div>
            <div class="flex-1 h-0 outline-1 outline-offset-[-0.50px] outline-neutral-300"/>
        </div>
        <div v-if="false">
            <div class="self-stretch inline-flex flex-col justify-start items-start gap-4">
                <UButton
                    class="self-stretch h-10 px-4 bg-neutral-100 rounded-[20px] inline-flex justify-center items-center gap-3 overflow-hidden hover:bg-neutral-50 active:bg-neutral-200 focus-visible:outline-3 focus-visible:outline-blue-300 focus-visible:outline-offset-0 disabled:bg-neutral-100 disabled:text-neutral-400 cursor-pointer"
                    icon="logos:google-icon"
                >
                    <span class="justify-start text-black text-sm font-medium font-dm-sans">Continue With Google</span>
                </UButton>
                <UButton
                    class="self-stretch h-10 px-4 bg-neutral-100 rounded-[20px] inline-flex justify-center items-center gap-3 overflow-hidden hover:bg-neutral-50 active:bg-neutral-200 focus-visible:outline-3 focus-visible:outline-blue-300 focus-visible:outline-offset-0 disabled:bg-neutral-100 disabled:text-neutral-400 cursor-pointer"
                    icon="logos:facebook"
                >
                    <span class="justify-start text-black text-sm font-medium font-dm-sans">Continue With Facebook</span>
                </UButton>
            </div>
        </div>

        <div class="self-stretch text-center justify-end">
            <span class="text-navy-600 text-sm font-normal font-dm-sans">Don’t have an account? </span>
            <span class="text-color-blue-900 text-sm font-medium font-dm-sans underline">
                <NuxtLink :to="localePath('domain-register')">Register</NuxtLink></span>
        </div>
    </section>
</template>

<script lang="ts" setup>

import type { InferOutput } from 'valibot'
import type { FormSubmitEvent } from '@nuxt/ui'
import type { AccountLoginRequest } from '~/types/api'
import { tenantKey } from '~/keys'

const apiClient = useApiClient()
const { setToken } = useAuthStore()
const localePath = useLocalePath()
const route = useRoute()
const v = useValibot()

const {tenant} = requiredInject(tenantKey)

const showPassword = ref(false)

const handleLogin = async ({
    data: formData,
}: FormSubmitEvent<LoginSchema>) => {
    submissionError.value = null

    const payload: AccountLoginRequest = {
        username: formData.username,
        password: formData.password,
    }

    try {
        const { data, error } = await apiClient.POST("/Auth", {
            body: payload,
        })

        if (error) throw error

        if (data.token != null) setToken(data.token)
        await navigateTo(`https://${route.params.domain as string}?token=${data.token as string}`, { external: true})
    } catch (error) {
        captureError(error)
        submissionError.value =
      (error as Error)?.message || "An error occurred. Please try again."
        window.scrollTo({ top: 0, behavior: "smooth" })
    }
}


const loginSchema = v.object({
    username: v.string(),
    password: v.string(),
})

type LoginSchema = InferOutput<typeof loginSchema>

const state = reactive<LoginSchema>({
    username: "",
    password: "",
})

const submissionError = ref<string | null>()

</script>